import { Breadcrumb } from "@progress/kendo-react-layout";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useHistory } from "react-router";
import { updateCurrUser } from "../../../actions/auth";
import { setContentManagerDataView } from "../../../actions/contentManager";
import { icon, label, number, route } from "../../../config";
import ProjectDetailsPopup from "../../Projects/ProjectDetailsPopup";
import QueueDetailsPopup from "../../Queues/QueueDetailsPopup";
import { getDefaultTaskValues } from "../../Tasks/tasks.service";
import { useContentManagerSystemContext } from "../ContentManagerSystemContext";
import {
  convertToBreadcrumbData,
  generateCmsAttachmentPath,
  mapData,
  resetContentManagerItems,
  restoreItemToParent,
  restoreMissingParents,
  searchTreeItem,
  useFolderHelpers,
} from "../helper";

/**
 * Parent Component: FileManagerContent
 *A wrapper around the Kendo React `Breadcrumb` component to display a navigable breadcrumb structure.
 * Allows users to select or navigate through breadcrumb items
 * @author Muskan Thakur
 */
export const BreadcrumbComponent = React.memo((props) => {
  const { contentManagerLibraryPath, fileMetaData } = useSelector((state) => state.contentManager);
  const [queueIdparam, setQueueId] = useState(null);
  const [isOwner, setIsOwner] = useState();
  const queueProjects = useSelector((state) => state.queueProjects);
  const [showProjectPopup, setShowProjectPopup] = useState(false);
  const [showQueuePopup, setShowQueuePopup] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { data } = useSelector((state) => state.allProjects);
  const { user } = useSelector((state) => state.auth);
  const projectClickRef = useRef();
  const queueClickRef = useRef();
  const breadcrumbRef = useRef();
  const allProjectsList = useSelector((state) => state.tasks.defaultDetails.allProjectsList);

  const { selectedItemPath } = useContentManagerSystemContext();

  const { fetchAndSetFolder } = useFolderHelpers();

  let pathname = location.pathname;
  const { params } = matchPath(pathname, {
    path: [route.PRIVATE_ROUTE.CMS.ROUTER_PATH, route.PRIVATE_ROUTE.Y_DOC.ROUTER_PATH],
  }) || { params: {} };

  const project = useMemo(() => {
    const resolvedProjectId = fileMetaData?.item?.projectId ?? user?.myProjectId;
    return allProjectsList?.find((i) => i.ProjectId == resolvedProjectId);
  }, [allProjectsList, fileMetaData?.item?.projectId, user?.myProjectId]);

  const fileProjectName = project ? (fileMetaData?.item?.projectId ? project.ProjectName : `${project.Name}'s ${label.BOARD}`) : "";

  const projectName = params?.projectName ?? fileProjectName;

  const isProjectLibraryItem = fileMetaData?.item?.projectId?.toString() ?? params?.projectId;

  const libraryName = useMemo(
    () => `${contentManagerLibraryPath}/${isProjectLibraryItem ? `${label.LIBRARY}` : `${label.MY_LIBRARY}`}`,
    [contentManagerLibraryPath, isProjectLibraryItem, projectName]
  );

  const showPopup = useMemo(() => {
    return isProjectLibraryItem || queueIdparam;
  }, [isProjectLibraryItem, queueIdparam]);

  const reversedMissingParents = fileMetaData?.missingParents ? [...fileMetaData.missingParents].reverse() : [];
  const path =
    pathname.includes("cms") && fileMetaData?.item
      ? generateCmsAttachmentPath(reversedMissingParents || [], fileMetaData?.item, libraryName)
      : props.item && selectedItemPath
      ? selectedItemPath
      : props?.data?.path;

  const [firstBreadCrumb, secondBreadCrumb = ""] = contentManagerLibraryPath?.split("/");

  /** Determine the project name and queue name based on the current projectId */
  const { projectNameBreadCrumb, queueNameBreadCrumb } = useMemo(() => {
    return {
      projectNameBreadCrumb: isProjectLibraryItem ? secondBreadCrumb : firstBreadCrumb,
      queueNameBreadCrumb: isProjectLibraryItem ? firstBreadCrumb : secondBreadCrumb,
    };
  }, [contentManagerLibraryPath, isProjectLibraryItem]);

  const projectLibrary = projectNameBreadCrumb + " Library";

  const breadcrumbData = convertToBreadcrumbData(path, contentManagerLibraryPath);

  const breadcrumbClass = props.isButtonStyle ? "custom-breadcrumb cms-breadcrumb button-style" : `custom-breadcrumb cms-breadcrumb`;

  const truncateText = (text, limit) => {
    return text.length > limit ? text.substring(number.ZERO, limit) + "..." : text;
  };

  /**Process breadcrumb data to include truncated names and full names */
  const processedBreadcrumbData = useMemo(
    () =>
      breadcrumbData.map((item) => ({
        ...item,
        text: truncateText(item.name, number.TWENTY),
        fullName: item.name,
      })),
    [breadcrumbData, projectLibrary]
  );

  /** Personal CMS roots: hide terminal crumb when path ends at My Library or My Files Vault only. */
  const hiddenTerminalCrumbName = useMemo(() => {
    if ( !path) return null;
    const segments = path.split("/").filter(Boolean);
    const boardDepth = contentManagerLibraryPath?.split("/").filter(Boolean).length ?? 0;
    const last = segments[segments.length - 1];
    if (segments.length === boardDepth + 1 && last === label.MY_LIBRARY) return label.MY_LIBRARY;
    if (segments.length === boardDepth + 1 && last === label.LIBRARY) return label.LIBRARY;
    if (segments.length === boardDepth + 2 && last === label.VAULT) return label.VAULT;

    return null;
  }, [path, contentManagerLibraryPath]);

  /**
   * generates breadcrumb data for display.
   * if the input data contains more than five items, "..." is shown in place of remaining folder names
   */
  const displayBreadcrumbData = useMemo(() => {
    const items =
      processedBreadcrumbData.length > number.THREE
        ? [...processedBreadcrumbData.slice(-number.THREE)]
        : processedBreadcrumbData.map((item) => ({
            ...item,
            disabled: props?.item?.isDeleted,
          }));
    if (!hiddenTerminalCrumbName || items[items.length - 1]?.name !== hiddenTerminalCrumbName) return items;
    return items.slice(0, -1);
  }, [processedBreadcrumbData, hiddenTerminalCrumbName, props?.item?.isDeleted]);

  /**sets queue id for respective project */
  useEffect(() => {
    setQueueId(allProjectsList?.find((i) => i.ProjectId == isProjectLibraryItem)?.QueueId);
  }, [data, isProjectLibraryItem]);

  useEffect(() => {
    if (allProjectsList?.length === number.ZERO) dispatch(getDefaultTaskValues(user.companyId, user.id));
  }, [queueIdparam, isProjectLibraryItem]);

  /**
   * Handle clicking outside the project popup to close it
   */
  useEffect(() => {
    if (!showProjectPopup) return;
    const handleClickOutside = (event) => {
      if (event.target.closest(".project-details-popup-content") || event.target.closest(".notification-popup-content")) {
        return;
      }
      setShowProjectPopup(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showProjectPopup, showQueuePopup]);

  /**
   * Handle clicking outside the queue popup to close it
   */
  useEffect(() => {
    if (!showQueuePopup) return;
    const handleClickOutside = (event) => {
      if (
        event.target.closest(".queue-details-popup-content") ||
        event.target.closest(".create-project-popup") ||
        event.target.closest(".move-project-popup") ||
        event.target.closest(".add-workspace-owner-popup") ||
        event.target.closest(".more-menu-popup-queue") ||
        event.target.closest(".move-project-dropdown")
      ) {
        return;
      }
      setShowQueuePopup(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showQueuePopup, showProjectPopup]);

  /**
   * Handles the selection of a breadcrumb item.
   * It processes the selected breadcrumb, determines if it corresponds to the project or queue,
   * and triggers actions based on the selected item. It also invokes the parent callback with the event details.
   */
  const handleItemSelect = async (event) => {
    const path = event.id;
    const selectedItem = path.substring(path.lastIndexOf("/") + number.ONE);
    const clickedElement = event.nativeEvent?.target || event.target;

    if (selectedItem === projectNameBreadCrumb && !props.isButtonStyle) {
      if (isProjectLibraryItem) {
        projectClickRef.current = clickedElement;
        setShowProjectPopup(true);
        return;
      } else {
        const projectPath = isProjectLibraryItem
          ? `${route.PRIVATE_ROUTE.TASKS.PATH}/${isProjectLibraryItem}/${projectName}`
          : route.PRIVATE_ROUTE.LANDING_PAGE.PATH;
        history.push(projectPath);
        dispatch(updateCurrUser({ lastSelectedBoard: label.GRID }));
        return;
      }
    } else if (selectedItem === queueNameBreadCrumb && !props.isButtonStyle) {
      queueClickRef.current = clickedElement;
      setShowQueuePopup(true);
      return;
    }

    if (pathname.includes("cms")) {
      let restoredData = resetContentManagerItems();

      const restoredParent = searchTreeItem(
        restoredData,
        fileMetaData?.item?.parentId ? { id: fileMetaData?.item?.parentId } : { path: libraryName }
      );

      const mappedRestoredItem = mapData([fileMetaData?.item], restoredParent?.path)[0];
      const newItem = searchTreeItem(restoredData, { id: mappedRestoredItem?.id });
      const parentItem = searchTreeItem(restoredData, { id: mappedRestoredItem?.parentId });

      if (newItem || parentItem) {
        props.handleBreadcrumbSelection(event);
        if (parentItem) {
          await fetchAndSetFolder(parentItem.id, parentItem, isProjectLibraryItem);
        }
      } else {
        history.push(isProjectLibraryItem ? `${route.PRIVATE_ROUTE.CMS.PATH}/${isProjectLibraryItem}/${projectName}` : route.PRIVATE_ROUTE.CMS.PATH);
        restoreMissingParents(reversedMissingParents, restoredData, libraryName);
        const payload = {
          restoredItem: mappedRestoredItem,
          restoredData,
          libraryName,
          projectId: isProjectLibraryItem,
          fetchAndSetFolder,
          showItem: true,
        };
        restoreItemToParent(payload);
      }
      dispatch(setContentManagerDataView(label.CMS_GRID));
    } else {
      props.handleBreadcrumbSelection(event);
    }
  };

  /**Handles keyboard navigation for the breadcrumb items. Triggers the parent callback with the event details. */
  const handleKeyDown = (event) => {
    props.handleBreadcrumbSelection(event);
  };
  return (
    <>
      {!props.isButtonStyle && processedBreadcrumbData.length !== displayBreadcrumbData.length && (
        <span className='breadcrumb-right'> {icon.RIGHT_BREADCRUMB}</span>
      )}
      <Breadcrumb
        data={displayBreadcrumbData ? displayBreadcrumbData : []}
        onItemSelect={handleItemSelect}
        onKeyDown={handleKeyDown}
        style={{ margin: 0, padding: "5px", textDecoration: "none", paddingLeft: "2px" }}
        className={breadcrumbClass}
      />
      {showProjectPopup && showPopup && (
        <ProjectDetailsPopup
          show={showProjectPopup}
          onClose={() => setShowProjectPopup(false)}
          projectId={isProjectLibraryItem}
          anchor={projectClickRef}
        />
      )}
      {showQueuePopup && showPopup && (
        <QueueDetailsPopup show={showQueuePopup} onClose={() => setShowQueuePopup(false)} queueId={queueIdparam} anchor={queueClickRef} />
      )}
    </>
  );
});
